import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationDto, NotificationKindType } from '@brandschutz/shared';
import { Icon, type IconName } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { deleteNotification, listNotifications, markAllRead, markRead, unreadCount } from './api';

const KIND_ICON: Record<NotificationKindType, IconName> = {
  project_assigned: 'UserPlus',
  project_status_change: 'GitCommit',
  mention: 'AtSign',
  mail_suggestion: 'Mail',
  deadline_approaching: 'Clock',
  field_suggestion: 'Sparkles',
  version_created: 'GitBranch',
  comment_added: 'MessageSquare',
};

const REFETCH_INTERVAL_MS = 30_000;
const RELATIVE_FMT = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RELATIVE_FMT.format(diffSec, 'second');
  if (abs < 3600) return RELATIVE_FMT.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return RELATIVE_FMT.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 7) return RELATIVE_FMT.format(Math.round(diffSec / 86400), 'day');
  if (abs < 86400 * 30) return RELATIVE_FMT.format(Math.round(diffSec / (86400 * 7)), 'week');
  return RELATIVE_FMT.format(Math.round(diffSec / (86400 * 30)), 'month');
}

function formatBadge(n: number): string {
  if (n <= 0) return '';
  if (n > 99) return '99+';
  return String(n);
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const countQuery = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: unreadCount,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => listNotifications(false),
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteNotification(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data]);
  const unread = countQuery.data?.unread ?? 0;

  const groups = useMemo(() => {
    const unreadItems: NotificationDto[] = [];
    const readItems: NotificationDto[] = [];
    for (const n of items) {
      if (n.readAt) readItems.push(n);
      else unreadItems.push(n);
    }
    return { unreadItems, readItems: readItems.slice(0, 20) };
  }, [items]);

  const handleItemClick = (n: NotificationDto) => {
    if (!n.readAt) markReadMutation.mutate(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Benachrichtigungen"
        aria-expanded={open}
        title="Benachrichtigungen"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded text-ink-500 hover:bg-ink-100 hover:text-ink-800"
      >
        <Icon name="Bell" size={18} />
        {unread > 0 ? (
          <span
            aria-label={`${unread} ungelesen`}
            className={cn(
              'absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white',
              unread <= 9 ? 'h-4 w-4' : 'h-4',
            )}
          >
            {formatBadge(unread)}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Benachrichtigungen"
          className="absolute right-0 top-full z-40 mt-2 w-96 max-h-[70vh] overflow-auto rounded-lg border border-ink-200 bg-paper shadow-elev-3"
        >
          <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-ink-200 bg-paper px-4 py-3">
            <h2 className="text-sm font-semibold text-ink-900">Benachrichtigungen</h2>
            {unread > 0 ? (
              <button
                type="button"
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending}
                className="text-xs font-medium text-accent-700 hover:text-accent-800 disabled:opacity-50"
              >
                Alle als gelesen markieren
              </button>
            ) : null}
          </div>

          {listQuery.isLoading ? (
            <div className="px-4 py-6 text-center text-sm text-ink-500">Lade …</div>
          ) : listQuery.isError ? (
            <div className="px-4 py-6 text-center text-sm text-danger">
              Konnte Benachrichtigungen nicht laden.
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-sm text-ink-500">
              <Icon name="BellOff" size={20} className="text-ink-400" />
              <span>Keine Benachrichtigungen.</span>
            </div>
          ) : (
            <div>
              {groups.unreadItems.length > 0 ? (
                <Section title="Ungelesen">
                  {groups.unreadItems.map((n) => (
                    <NotificationRow
                      key={n.id}
                      notification={n}
                      onClick={() => handleItemClick(n)}
                      onDelete={() => deleteMutation.mutate(n.id)}
                    />
                  ))}
                </Section>
              ) : null}
              {groups.readItems.length > 0 ? (
                <Section title="Älter">
                  {groups.readItems.map((n) => (
                    <NotificationRow
                      key={n.id}
                      notification={n}
                      onClick={() => handleItemClick(n)}
                      onDelete={() => deleteMutation.mutate(n.id)}
                    />
                  ))}
                </Section>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="bg-ink-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
        {title}
      </h3>
      <ul className="divide-y divide-ink-100">{children}</ul>
    </div>
  );
}

interface RowProps {
  notification: NotificationDto;
  onClick: () => void;
  onDelete: () => void;
}

function NotificationRow({ notification, onClick, onDelete }: RowProps) {
  const iconName = KIND_ICON[notification.kind] ?? 'Bell';
  const isUnread = !notification.readAt;
  return (
    <li
      className={cn(
        'group flex items-start gap-3 px-4 py-3 transition-colors',
        isUnread ? 'bg-accent-50/40 hover:bg-accent-50' : 'hover:bg-ink-50',
      )}
    >
      <button type="button" onClick={onClick} className="flex flex-1 items-start gap-3 text-left">
        <span
          className={cn(
            'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            isUnread ? 'bg-accent-100 text-accent-700' : 'bg-ink-100 text-ink-600',
          )}
        >
          <Icon name={iconName} size={16} />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block text-sm leading-snug',
              isUnread ? 'font-semibold text-ink-900' : 'text-ink-800',
            )}
          >
            {notification.title}
          </span>
          {notification.body ? (
            <span className="mt-0.5 block truncate text-xs text-ink-500">{notification.body}</span>
          ) : null}
          <span className="mt-1 block text-[10px] uppercase tracking-wide text-ink-400">
            {relativeTime(notification.createdAt)}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Benachrichtigung löschen"
        title="Löschen"
        className="invisible mt-1 inline-flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-ink-200 hover:text-ink-700 group-hover:visible"
      >
        <Icon name="X" size={14} />
      </button>
    </li>
  );
}
